import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

export const maxSize = 200 * 1024 * 1024; // 20 MiB

interface DropzoneProps {
  onDrop: (acceptedFiles: File[]) => void;
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

// const focusedStyle = {
//   borderColor: '#2196f3'
// };

// const acceptStyle = {
//   borderColor: '#00e676'
// };

// const rejectStyle = {
//   borderColor: '#ff1744'
// };

const StyledDropzone = (props: DropzoneProps) => {
  const { t } = useTranslation();
  const { onDrop } = props;
  const { acceptedFiles, getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      maxSize,
      multiple: false,
      onDrop
    });

  const { borderColor, bgcolor } = useMemo(() => {
    if (isDragReject) return { borderColor: 'error.main', bgcolor: 'error.lighter' };
    if (isDragAccept) return { borderColor: 'success.main', bgcolor: 'success.lighter' };
    if (isFocused) return { borderColor: 'primary.main', bgcolor: 'background.paper' };
    return { borderColor: 'divider', bgcolor: 'background.paper' };
  }, [isFocused, isDragAccept, isDragReject]);

  const file = acceptedFiles[0];

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Zona drop ── */}
      <Box
        {...getRootProps()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          p: 4,
          borderWidth: 2,
          borderRadius: 2,
          borderStyle: 'dashed',
          borderColor,
          bgcolor,
          color: 'text.secondary',
          outline: 'none',
          cursor: 'pointer',
          textAlign: 'center',
          minHeight: 140,
          transition: (theme) =>
            theme.transitions.create(['border-color', 'background-color'], {
              duration: theme.transitions.duration.short
            }),
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover'
          }
        }}>
        <input {...getInputProps()} aria-label={t('actions.upload-attachment')} />
        <CloudUploadIcon sx={{ fontSize: 26, color: 'action.active' }} />
        <Typography variant="body1">{t('actions.drop-files')}</Typography>
        <Typography variant="caption" color="text.disabled">
          {t('actions.max-file-size', { size: '20 MiB' })}
        </Typography>
      </Box>

      {/* ── Riepilogo file accettato ── */}
      {file && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider'
          }}>
          <InsertDriveFileIcon fontSize="small" color="action" />
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="body2" noWrap title={file.name}>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {file.size > 0 ? `${(file.size / 1024).toFixed(1)} KB` : ''}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
// const style = useMemo(
//   () => ({
//     ...baseStyle,
//     ...(isFocused ? focusedStyle : {}),
//     ...(isDragAccept ? acceptStyle : {}),
//     ...(isDragReject ? rejectStyle : {})
//   }),
//   [isFocused, isDragAccept, isDragReject]
// );

// return (
// <div className="container">
//   <div {...getRootProps({ style })}>
//     <input {...getInputProps()} />
//     <p>{t('actions.drop-files')}</p>
//   </div>
//   {acceptedFiles.map((file) => (
//     <li key={file.name}>
//       {file.name} - {file.size} bytes
//     </li>
//   ))}
// </div>
//   );
// };

export default StyledDropzone;
